/**
 * Route guard for signed-in pages. Same rules as today's ProtectedRoute (src/legacy/components/layout/ProtectedRoute.tsx):
 *  - not signed in -> enter page (an applicant -> their application);
 *  - INVITED_PENDING_SETUP etc. (requiresOnboarding) -> Home, where the onboarding steps run;
 *  - no access on a page that needs it -> the lock screen with today's checkout window (SubscriptionOnboardingModal);
 *  - trial/grace ending -> today's SubscriptionExpiringModal; banner -> a quiet strip.
 * Home and Settings never need a subscription (same as today's /welcome and /account).
 */
import { useEffect, useState } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useAuth } from '@/features/auth'
import { useAccessLevel } from '@/hooks/useAccessLevel'
import { SubscriptionOnboardingModal } from '@/components/auth/SubscriptionOnboardingModal'
import { SubscriptionExpiringModal } from '@/components/subscription/SubscriptionExpiringModal'
import { Icon } from './ui'
import { useAuthReady } from './session'

const NO_SUBSCRIPTION_NEEDED = ['/home', '/settings']
const EXPIRING = ['TRIAL_ENDING_SOON', 'TRIAL_EXPIRED_GRACE', 'SUBSCRIPTION_EXPIRED_GRACE']

export function Member() {
  const { isAuthenticated, isApplicant, user } = useAuth()
  const ready = useAuthReady()
  const { pathname } = useLocation()
  const access = useAccessLevel({ skip: !isAuthenticated || !user })
  const [dismissed, setDismissed] = useState(false)
  const [checkout, setCheckout] = useState(false)
  const [revalidated, setRevalidated] = useState(false)
  const free = NO_SUBSCRIPTION_NEEDED.some((p) => pathname.startsWith(p))

  // Same as today (ProtectedRoute): before showing the lock, ask the server once more (it double-checks Stripe), then
  // open the checkout window by itself; both reset when she goes to another page
  useEffect(() => { setRevalidated(false); setCheckout(false) }, [pathname])
  useEffect(() => {
    if (!revalidated && !access.loading && isAuthenticated && user && !access.hasAccess && !free && !access.requiresOnboarding) {
      setRevalidated(true)
      // today's Gallery sits outside the guard and only shows "Subscription required" (no window by itself)
      access.refresh().then(() => { if (!pathname.startsWith('/gallery')) setCheckout(true) })
    }
  }, [revalidated, access.loading, access.hasAccess, isAuthenticated, user, free, access.requiresOnboarding])

  if (!ready || (isAuthenticated && access.loading)) return <Waiting />
  if (!isAuthenticated) return <Navigate to={isApplicant ? '/apply' : '/'} replace />
  if (access.error) {
    return (
      <main className="wrap" id="main" style={{ paddingTop: 80, textAlign: 'center' }}>
        <p className="subline">We couldn’t check your access just now.</p>
        <button className="btn btn-sm" type="button" onClick={() => window.location.reload()} style={{ marginTop: 16 }}>Try again</button>
      </main>
    )
  }
  if (access.requiresOnboarding && !free) return <Navigate to="/home" replace />

  if (!access.hasAccess && !free) {
    return (
      <main className="wrap" id="main" style={{ paddingTop: 72, paddingBottom: 72 }}>
        <article className="card conversation on-dark" style={{ maxWidth: 560, margin: '0 auto', textAlign: 'center' }}>
          <div className="pin-lock" aria-hidden="true" style={{ margin: '0 auto 12px' }}><Icon id="i-lock" /></div>
          <h2 style={{ fontFamily: 'var(--cw-font-editorial)', fontWeight: 400, fontSize: '2rem' }}>Your access has ended</h2>
          <p style={{ margin: '10px 0 22px', opacity: 0.85 }}>Renew to open my texts, the gallery and your vault again.</p>
          <button className="btn btn-light btn-cta" type="button" onClick={() => setCheckout(true)}><em>Renew my access</em></button>
        </article>
        {checkout && user && (
          <div className="tw-scope">
            <SubscriptionOnboardingModal isOpen userId={user.id} inviteToken={null} allowSkip={false}
              onClose={() => setCheckout(false)}
              onComplete={() => { setCheckout(false); access.refresh(); window.location.reload() }} />
          </div>
        )}
      </main>
    )
  }

  const days = access.trialDaysRemaining ?? (access.state === 'SUBSCRIPTION_EXPIRED_GRACE' && access.expiresAt
    ? Math.ceil((new Date(access.expiresAt).getTime() - Date.now()) / 864e5) : null)

  return (
    <>
      {access.showBanner && access.bannerMessage && (
        <div className="secure-strip" role="status"><div className="wrap"><Icon id="i-clock" /><span>{access.bannerMessage}</span></div></div>
      )}
      {!dismissed && EXPIRING.includes(access.state) && (
        <div className="tw-scope">
          <SubscriptionExpiringModal state={access.state} daysRemaining={days} expiresAt={access.expiresAt} onDismiss={() => setDismissed(true)} />
        </div>
      )}
      <Outlet />
    </>
  )
}

export function Waiting({ label = 'Opening your lounge…' }: { label?: string }) {
  return (
    <main className="wrap" id="main" style={{ minHeight: '60vh', display: 'grid', placeItems: 'center' }}>
      <p className="eyebrow" role="status">{label}</p>
    </main>
  )
}
